// MetricsTable.jsx

import React, { useState, useMemo, useRef, useEffect } from 'react';
import MetricsRow from './MetricsRow';
// import ExcelUploader from './ExcelUploader'; // STARO: Upload za svaki dan - sada u modalu "Upravljaj uploadanim danima"
import '../css/MetricsTable.css';
import { db } from '../config/firebase';
import { doc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';

// Format decimalnih sati u "Xh Ymin"
const formatDuration = (decimalHours) => {
  if (decimalHours == null || isNaN(decimalHours) || decimalHours <= 0) return '0h';
  const h = Math.floor(decimalHours);
  const m = Math.round((decimalHours - h) * 60);
  if (h === 0 && m === 0) return '0h';
  return `${h}h ${m}min`;
};

function MetricsTable({
  people = [],
  dailyMetricsData = [],
  selectedNationality,
  selectedFirm,
  selectedYear,
  selectedMonth,
  refreshData,
  firms = [],
  updateDailyMetrics,
  highlightedDays = [],
  onToggleDayHighlight,
  metrickePostavke = {},
}) {
  const daysInMonth = 35;
  const [uploadedDates, setUploadedDates] = useState({});

  // Default vrijednosti za stupce
  const defaultHiddenColumns = {
    riderID: false,
    grad: false,
    firma: false,
    nationality: false,
    suma: false,
    sumaRadnihSati: false,
    dnevniProsjek: false,
    dani: false,
  };

  // Učitaj spremljene postavke iz localStorage pri inicijalizaciji
  const [hiddenColumns, setHiddenColumns] = useState(() => {
    try {
      const saved = localStorage.getItem('metricsTableColumns');
      if (saved) {
        const parsed = JSON.parse(saved);
        return { ...defaultHiddenColumns, ...parsed };
      }
    } catch (e) {
      console.warn('Greška pri učitavanju postavki stupaca:', e);
    }
    return defaultHiddenColumns;
  });

  // Spremi postavke u localStorage kad se promijene
  useEffect(() => {
    try {
      localStorage.setItem('metricsTableColumns', JSON.stringify(hiddenColumns));
    } catch (e) {
      console.warn('Greška pri spremanju postavki stupaca:', e);
    }
  }, [hiddenColumns]);

  // Stanje za otvorenost dropdown menija
  const [isColumnMenuOpen, setIsColumnMenuOpen] = useState(false);

  // Zatvaranje dropdown menija na ESC tipku
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isColumnMenuOpen) {
        setIsColumnMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isColumnMenuOpen]);

  const topScrollRef = useRef(null);
  const bottomScrollRef = useRef(null);
  const tableRef = useRef(null);

  // Synchronize scroll between top and bottom
  const syncScroll = (source) => {
    if (source === 'top' && topScrollRef.current && bottomScrollRef.current) {
      bottomScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    } else if (source === 'bottom' && topScrollRef.current && bottomScrollRef.current) {
      topScrollRef.current.scrollLeft = bottomScrollRef.current.scrollLeft;
    }
  };

  // Funkcija za toggle vidljivosti stupca
  const toggleColumn = (columnName) => {
    setHiddenColumns((prev) => ({
      ...prev,
      [columnName]: !prev[columnName],
    }));
  };

  // Definicija svih stupaca s prijateljskim imenima
  const allColumns = [
    { key: 'riderID', label: 'Rider ID' },
    { key: 'grad', label: 'Grad' },
    { key: 'firma', label: 'Firma' },
    { key: 'nationality', label: 'Nacionalnost' },
    { key: 'suma', label: 'Suma' },
    { key: 'sumaRadnihSati', label: 'Suma Ukupno Sati' },
    { key: 'dnevniProsjek', label: 'Dnevni Prosjek' },
    { key: 'dani', label: 'Dani (1-35)' },
  ];

  // Toggle pin status for a user
  const togglePinUser = async (userId) => {
    try {
      const person = people.find(p => p.id === userId);
      if (!person) return;

      const newPinStatus = !(person.isPinned === true);
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        isPinned: newPinStatus
      });

      // Osvježi podatke nakon promjene
      refreshData();
    } catch (error) {
      console.error('Greška pri ažuriranju pin statusa:', error);
      alert('Došlo je do greške pri ažuriranju pin statusa.');
    }
  };

  // Filter and sort people
  const filteredPeople = useMemo(() => {
    let filtered = [...people];

    // Filtriranje po nacionalnosti
    if (selectedNationality !== 'Sve') {
      filtered = filtered.filter((person) => person.nationality === selectedNationality);
    }

    // Filtriranje po firmi
    if (selectedFirm) {
      filtered = filtered.filter((person) => person.firmId === selectedFirm);
    }

    // Filtriranje arhiviranih osoba
    filtered = filtered.filter((person) => {
      if (person.archived) {
        const archiveDate = new Date(person.archiveYear, person.archiveMonth - 1);
        const selectedDate = new Date(selectedYear, selectedMonth - 1);
        return archiveDate > selectedDate;
      }
      return true;
    });

    // Filtriranje po početnom datumu
    filtered = filtered.filter((person) => {
      const startDate = new Date(person.startYear, person.startMonth - 1);
      const selectedDate = new Date(selectedYear, selectedMonth - 1);
      return startDate <= selectedDate;
    });

    // Sortiranje - prvo pinani korisnici, zatim po firmi
    return filtered.sort((a, b) => {
      // Prvo provjeri jesu li pinani (eksplicitno provjeri boolean vrijednost)
      const isPinnedA = a.isPinned === true;
      const isPinnedB = b.isPinned === true;
      
      if (isPinnedA && !isPinnedB) return -1;
      if (!isPinnedA && isPinnedB) return 1;
      
      // Ako su oba pinana ili oba nisu pinana, sortiraj po firmi
      const firmA = firms.find(f => f.id === a.firmId)?.name || '';
      const firmB = firms.find(f => f.id === b.firmId)?.name || '';
      return firmA.localeCompare(firmB);
    });
  }, [people, selectedNationality, selectedFirm, selectedYear, selectedMonth, firms]);

  // Calculate total and daily sums (SATI - tablica sati)
  const {
    totalActualWorkingHours,
    dailySums,
    weekSums,
    averageTotalWorkingHoursPerPerson,
    /* STARO: za tablicu zarada - personTotals, dailyAverageAcrossAllDays, overallAverage, totalSum */
  } = useMemo(() => {
    let glovoTotal = 0;
    let woltTotal = 0;
    const dailyTotals = Array(daysInMonth).fill(0); // Suma sati po danu (glovo+wolt)
    const personTotalWorkingHoursArray = [];

    filteredPeople.forEach((person) => {
      const personMetrics = dailyMetricsData.filter(
        (metric) =>
          metric.userId === person.id &&
          metric.year === selectedYear &&
          metric.month === selectedMonth
      );

      let personGlovo = 0;
      let personWolt = 0;
      personMetrics.forEach((m) => {
        const glovoH = m.glovoHours ?? (m.actualWorkingHours ? parseFloat(m.actualWorkingHours) : 0);
        const woltH = m.woltHours ?? (m.plannedWorkingHours ? parseFloat(m.plannedWorkingHours) : 0);
        personGlovo += parseFloat(glovoH) || 0;
        personWolt += parseFloat(woltH) || 0;
      });
      const personTotalHours = personGlovo + personWolt;
      glovoTotal += personGlovo;
      woltTotal += personWolt;
      personTotalWorkingHoursArray.push(personTotalHours);

      // Akumuliraj sati po danu (umjesto zarade)
      personMetrics.forEach((metric) => {
        const dayIndex = metric.day - 1;
        const glovoH = metric.glovoHours ?? (metric.actualWorkingHours ? parseFloat(metric.actualWorkingHours) : 0);
        const woltH = metric.woltHours ?? (metric.plannedWorkingHours ? parseFloat(metric.plannedWorkingHours) : 0);
        const hours = (parseFloat(glovoH) || 0) + (parseFloat(woltH) || 0);
        dailyTotals[dayIndex] += hours;
      });
    });

    const averageTotalWorkingHoursPerPerson =
      personTotalWorkingHoursArray.length > 0
        ? personTotalWorkingHoursArray.reduce((sum, hours) => sum + hours, 0) /
          personTotalWorkingHoursArray.length
        : 0;

    // Suma po tjednima (tjedan 1 = dani 1-7, itd.) - u satima
    const numWeeks = Math.ceil(daysInMonth / 7);
    const weekSums = Array.from({ length: numWeeks }, (_, w) => {
      const start = w * 7;
      const end = Math.min(start + 7, daysInMonth);
      return dailyTotals.slice(start, end).reduce((s, v) => s + v, 0);
    });

    return {
      totalActualWorkingHours: glovoTotal + woltTotal,
      dailySums: dailyTotals,
      weekSums,
      averageTotalWorkingHoursPerPerson,
    };
  }, [filteredPeople, dailyMetricsData, selectedYear, selectedMonth, daysInMonth]);

  /* STARO: Logika za tablicu zarada - totalSum, dailyAverageAcrossAllDays, overallAverage, personTotals
  const {
    totalSum,
    dailyAverageAcrossAllDays,
    overallAverage,
  } = useMemo(() => {
    let total = 0;
    const personTotals = [];
    const averages = [];
    filteredPeople.forEach((person) => {
      const personMetrics = dailyMetricsData.filter(...);
      const personTotal = personMetrics.reduce((sum, m) => sum + (parseFloat(m.amount) || 0), 0);
      personTotals.push(personTotal);
      const daysWorked = personMetrics.filter((m) => m.amount !== undefined && m.amount !== null).length;
      const averageEarnings = daysWorked > 0 ? personTotal / daysWorked : 0;
      averages.push(averageEarnings);
      personMetrics.forEach((metric) => { dailyTotals[dayIndex] += amount; total += amount; });
    });
    return { totalSum: total, dailyAverageAcrossAllDays, overallAverage };
  }, [...]);
  */

  // Funkcija za parsiranje datuma iz imena filea
  const extractDateFromFilename = (filename) => {
    // Traži pattern dd.mm ili dd.mm.yyyy
    const datePattern = /(\d{1,2})\.(\d{1,2})(\.(\d{4}))?/;
    const match = filename.match(datePattern);
    
    if (match) {
      const day = parseInt(match[1]);
      const month = parseInt(match[2]);
      const year = match[4] ? parseInt(match[4]) : selectedYear;
      
      return { day, month, year };
    }
    return null;
  };

  // Funkcija za dobivanje dana u tjednu
  const getDayOfWeek = (day, month, year) => {
    const date = new Date(year, month - 1, day);
    const daysOfWeek = ['Ned', 'Pon', 'Uto', 'Sri', 'Čet', 'Pet', 'Sub'];
    return daysOfWeek[date.getDay()];
  };

  // Dohvati informacije o uploadanim danima
  useEffect(() => {
    const fetchUploadedDays = async () => {
      try {
        const uploadedDaysRef = collection(db, 'uploadedDays');
        const q = query(
          uploadedDaysRef,
          where('year', '==', selectedYear),
          where('month', '==', selectedMonth)
        );
        
        const querySnapshot = await getDocs(q);
        const dates = {};
        
        querySnapshot.docs.forEach(doc => {
          const data = doc.data();
          const extractedDate = extractDateFromFilename(data.filename);
          
          if (extractedDate && extractedDate.month === selectedMonth) {
            dates[data.day] = {
              filename: data.filename,
              date: extractedDate,
              dayOfWeek: getDayOfWeek(extractedDate.day, extractedDate.month, extractedDate.year)
            };
          }
        });
        
        setUploadedDates(dates);
      } catch (error) {
        console.error('Greška pri dohvaćanju uploadanih dana:', error);
      }
    };

    fetchUploadedDays();
  }, [selectedYear, selectedMonth]);

  // Funkcija za formatiranje datuma
  const formatDate = (day, month) => {
    return `${day.toString().padStart(2, '0')}.${month.toString().padStart(2, '0')}`;
  };

  return (
    <div className="metrics-table-container">
      <div className="table-header">
        <span className="people-count">
          Prikazano osoba: {filteredPeople.length}
        </span>
        <div className="column-toggle-wrapper">
          <button
            className="column-toggle-button"
            onClick={() => setIsColumnMenuOpen(!isColumnMenuOpen)}
          >
            <span>Prikaži stupce</span>
            <span className={`dropdown-arrow ${isColumnMenuOpen ? 'open' : ''}`}>▼</span>
          </button>
          {isColumnMenuOpen && (
            <>
              <div
                className="dropdown-overlay"
                onClick={() => setIsColumnMenuOpen(false)}
              />
              <div className="column-dropdown-menu">
                <div className="dropdown-header">
                  <h3>Odaberi stupce za prikaz</h3>
                  <button
                    className="close-dropdown"
                    onClick={() => setIsColumnMenuOpen(false)}
                  >
                    ×
                  </button>
                </div>
                <div className="dropdown-content">
                  {allColumns.map((column) => (
                    <label key={column.key} className="dropdown-item">
                      <input
                        type="checkbox"
                        checked={!hiddenColumns[column.key]}
                        onChange={() => toggleColumn(column.key)}
                      />
                      <span>{column.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <div className="table-wrapper">
        <table className="metrics-table" ref={tableRef}>
          <thead>
            <tr className="summary-row">
              <td>
                <strong>Suma</strong>
              </td>
              <td></td>
              <td className={hiddenColumns.riderID ? 'hidden-column' : ''}></td>
              <td className={hiddenColumns.grad ? 'hidden-column' : ''}></td>
              <td className={hiddenColumns.firma ? 'hidden-column' : ''}></td>
              <td className={hiddenColumns.nationality ? 'hidden-column' : ''}></td>
              {/* STARO: Stupac Suma (zarada) - tablica zarada */}
              {/* <td className={hiddenColumns.suma ? 'hidden-column' : ''}><strong>{totalSum.toFixed(2)}</strong></td> */}
              <td className={hiddenColumns.sumaRadnihSati ? 'hidden-column' : ''}>
                <strong>{formatDuration(totalActualWorkingHours)}</strong>
              </td>
              {/* STARO: Stupac Dnevni prosjek */}
              {/* <td className={hiddenColumns.dnevniProsjek ? 'hidden-column' : ''}></td> */}
              {!hiddenColumns.dani && (() => {
                const cells = [];
                const numWeeks = Math.ceil(daysInMonth / 7);
                for (let w = 0; w < numWeeks; w++) {
                  for (let d = 0; d < 7 && w * 7 + d < daysInMonth; d++) {
                    const idx = w * 7 + d;
                    const dayNum = idx + 1;
                    cells.push(
                      <td key={`d-${idx}`} className={highlightedDays.includes(dayNum) ? 'day-highlighted' : ''}>
                        <strong>{formatDuration(dailySums[idx])}</strong>
                      </td>
                    );
                  }
                  cells.push(
                    <td key={`w-${w}`} className="week-total-cell">
                      <strong>{formatDuration(weekSums[w])}</strong>
                    </td>
                  );
                }
                return cells;
              })()}
            </tr>
            <tr className="average-sum-row">
              <td>
                <strong>Prosjek Suma</strong>
              </td>
              <td></td>
              <td className={hiddenColumns.riderID ? 'hidden-column' : ''}></td>
              <td className={hiddenColumns.grad ? 'hidden-column' : ''}></td>
              <td className={hiddenColumns.firma ? 'hidden-column' : ''}></td>
              <td className={hiddenColumns.nationality ? 'hidden-column' : ''}></td>
              {/* STARO: Stupac Suma - prosjek zarade po danu */}
              {/* <td className={hiddenColumns.suma ? 'hidden-column' : ''}><strong>{dailyAverageAcrossAllDays.toFixed(2)}</strong></td> */}
              <td className={hiddenColumns.sumaRadnihSati ? 'hidden-column' : ''}>
                <strong>{formatDuration(averageTotalWorkingHoursPerPerson)}</strong>
              </td>
              {/* STARO: Stupac Dnevni prosjek - prosječna zarada po danu rada */}
              {/* <td className={hiddenColumns.dnevniProsjek ? 'hidden-column' : ''}><strong>{overallAverage.toFixed(2)}</strong></td> */}
              {!hiddenColumns.dani && (() => {
                const cells = [];
                const numWeeks = Math.ceil(daysInMonth / 7);
                for (let w = 0; w < numWeeks; w++) {
                  for (let d = 0; d < 7 && w * 7 + d < daysInMonth; d++) {
                    const dayNum = w * 7 + d + 1;
                    cells.push(
                      <td key={`d-${w * 7 + d}`} className={highlightedDays.includes(dayNum) ? 'day-highlighted' : ''}></td>
                    );
                  }
                  cells.push(<td key={`w-${w}`} className="week-total-cell"></td>);
                }
                return cells;
              })()}
            </tr>
            <tr>
              <th>Ime</th>
              <th>PIN</th>
              <th className={hiddenColumns.riderID ? 'hidden-column' : ''}>Rider ID</th>
              <th className={hiddenColumns.grad ? 'hidden-column' : ''}>Grad</th>
              <th className={hiddenColumns.firma ? 'hidden-column' : ''}>Firma</th>
              <th className={hiddenColumns.nationality ? 'hidden-column' : ''}>Nacionalnost</th>
              {/* STARO: Stupac Suma (zarada) */}
              {/* <th className={hiddenColumns.suma ? 'hidden-column' : ''}>Suma</th> */}
              <th className={hiddenColumns.sumaRadnihSati ? 'hidden-column' : ''}>Suma Ukupno Sati</th>
              {/* STARO: Stupac Dnevni prosjek (zarada) */}
              {/* <th className={hiddenColumns.dnevniProsjek ? 'hidden-column' : ''}>Dnevni Prosjek</th> */}
              {!hiddenColumns.dani && (() => {
                const headers = [];
                const numWeeks = Math.ceil(daysInMonth / 7);
                for (let w = 0; w < numWeeks; w++) {
                  for (let d = 1; d <= 7; d++) {
                    const day = w * 7 + d;
                    if (day > daysInMonth) break;
                    headers.push(
                      <th
                        key={`day-${day}`}
                        className={`day-header ${highlightedDays.includes(day) ? 'day-highlighted' : ''}`}
                        onClick={() => onToggleDayHighlight?.(day)}
                      >
                        <div>Dan {day}</div>
                        {uploadedDates[day] && (
                          <div className="date-info">
                            <div>{uploadedDates[day].dayOfWeek}</div>
                            <div>{formatDate(uploadedDates[day].date.day, uploadedDates[day].date.month)}</div>
                          </div>
                        )}
                        {/* STARO: Upload za svaki dan - tablica zarada, upload prebačen u modal "Upravljaj uploadanim danima" */}
                        {/* <span onClick={(e) => e.stopPropagation()}>
                          <ExcelUploader onDataParsed={(data, filename) => updateDailyMetrics(day, data, filename)}>
                            {uploadedDates[day] ? 'Promijeni' : 'Upload'}
                          </ExcelUploader>
                        </span> */}
                      </th>
                    );
                  }
                  headers.push(
                    <th key={`week-${w}`} className="week-total-header">
                      Tjedan {w + 1} ukupno
                    </th>
                  );
                }
                return headers;
              })()}
            </tr>
          </thead>
          <tbody>
            {filteredPeople.map((person) => (
                <MetricsRow
                  key={person.id}
                  person={person}
                  dailyMetricsData={dailyMetricsData.filter(
                    (metric) =>
                      metric.userId === person.id &&
                      metric.year === selectedYear &&
                      metric.month === selectedMonth
                  )}
                  selectedYear={selectedYear}
                  selectedMonth={selectedMonth}
                  refreshData={refreshData}
                  firms={firms}
                  metrickePostavke={metrickePostavke}
                  daysInMonth={daysInMonth}
                  isPinned={person.isPinned === true}
                  onTogglePin={() => togglePinUser(person.id)}
                  hiddenColumns={hiddenColumns}
                  highlightedDays={highlightedDays}
                />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default MetricsTable;
